import {
  useState,
  useEffect,
  Ref,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "reactstrap";
import { Tooltip, Whisper } from "rsuite";

import DisplayRecord from "./DisplayRecord";
import RichTextEditorV2 from "../CRM/RichTextEditorV2";
import AppDrawer from "../Drawers/AppDrawer";
import AppSpinner from "../../elements/AppSpinner";
import EmptyField from "./EmptyField";
import IconButton from "../../elements/IconButton";
import { newToastEvent } from "../../utils/ToastUtil";
import StrUtils from "../../utils/StrUtils";
import RightArrow from "../../elements/RightArrow";

interface Props {
  tableView: boolean;
  recordID: string;
  field: any;
  value: string;
  plaintext: string;
  modifyTrigger: "click" | "doubleclick";
  mode: "r" | "rw";
  onUpdate: (payload: any) => void;
  submitting: boolean;
}

const RichTextRecord = (
  {
    tableView,
    recordID,
    field,
    value,
    plaintext,
    modifyTrigger,
    mode,
    onUpdate,
    submitting,
  }: Props,
  ref?: Ref<HTMLInputElement>
) => {
  const [editDrawer, setEditDrawer] = useState(false);
  const [richText, setRichText] = useState<string | undefined>();
  const [editingText, setEditingText] = useState(false);
  const readonly = mode === "r";

  const openModify = (editing = true) => {
    setRichText(value);
    setEditDrawer(true);
    setEditingText(editing);
  };

  const save = () => {
    if (value === richText) return closeModify();

    onUpdate({
      field,
      recordID,
      payload: {
        [field.APIName]: {
          RichText: richText ? richText : "",
          PlainText: richText
            ? StrUtils.convertMarkupToPlaintext(richText)
            : "",
        },
      },
      onSuccess: () => {
        closeModify();

        newToastEvent("success", `Successfully updated ${field.Name}.`);
      },
    });
  };

  const closeModify = () => {
    setEditDrawer(false);
    setRichText("");
  };

  // If there is no value, automatically start editing the document!
  useEffect(() => {
    if (!editDrawer) return;

    setEditingText(!value);
  }, [editDrawer, value]);

  useEffect(() => {
    setRichText(value);
  }, [value]);

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    focus: () => {
      openModify();
    },
  }));

  return (
    <>
      <DisplayRecord
        mode={mode}
        tableView={tableView}
        modifyTrigger={modifyTrigger}
        openModify={openModify}
        openView={() => openModify(false)}
        isSensitive={field?.Sensitive ?? false}
        displayComponent={(_: any, innerRef: any) =>
          field &&
          (value ? (
            <>
              <Whisper
                placement="autoVerticalStart"
                trigger="hover"
                delayOpen={150}
                speaker={plaintext ? <Tooltip>{plaintext}</Tooltip> : <></>}
              >
                <div
                  className="tw:text-dark tw:max-w-92 tw:truncate"
                  onClick={readonly ? () => openModify() : undefined}
                  onDoubleClick={readonly ? undefined : () => openModify()}
                  ref={innerRef}
                >
                  {plaintext ? (
                    <div className="tw:truncate">
                      {plaintext.trim().length ? (
                        plaintext
                      ) : (
                        <>
                          Preview
                          <RightArrow />
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      Preview
                      <RightArrow />
                    </>
                  )}
                </div>
              </Whisper>
            </>
          ) : (
            <div className="pr-4" onDoubleClick={() => openModify()}>
              <EmptyField table={tableView} />
            </div>
          ))
        }
      />
      <AppDrawer
        open={editDrawer}
        onClose={() => setEditDrawer(false)}
        headerTitle={field.Name}
        headerOptions={
          readonly ? undefined : (
            <>
              {editingText ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingText(false);
                      setRichText(value);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={save}
                    disabled={submitting}
                    style={{ width: 85 }}
                  >
                    {submitting ? <AppSpinner color="white" /> : "Save"}
                  </Button>
                </>
              ) : (
                <IconButton
                  icon="pencil"
                  onClick={() => setEditingText(true)}
                />
              )}
            </>
          )
        }
        bodyCSS="mt--3"
        body={
          <>
            <RichTextEditorV2
              isEditing={readonly ? false : editingText && !submitting}
              value={richText ?? ""}
              onChange={(val: string) => setRichText(val)}
              toggleEdit={() => setEditingText(true)}
            />
          </>
        }
      />
    </>
  );
};

export default forwardRef(RichTextRecord);
